/**
 * RecordAdministrationModal
 * Modal for recording a vaccine administration for a traveler, launched from
 * a vaccine selection row. On submit the administration is saved and queued
 * for CAIR; if the traveler is missing required demographics the modal shows
 * a warning with a shortcut to fix them and resubmit.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  cairService,
  notifyAdministrationsUpdated,
  CairReferenceCodes,
  CvxMapping,
  RecordAdministrationRequest,
} from "@/services/cair";
import { CairDemographicsForm } from "./CairDemographicsForm";

interface RecordAdministrationModalProps {
  isOpen: boolean;
  travelerId: string;
  /** The vaccine selection's item_key / protocol key */
  protocolKey: string;
  /** Display name for the vaccine (falls back to CVX description) */
  vaccineName?: string;
  /** Optional recommendation selection id to link the administration to */
  recommendationSelectionId?: string;
  onClose: () => void;
  /** Called after the administration is successfully recorded */
  onRecorded?: () => void;
}

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "6px",
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: "13px",
  color: "#1f2937",
  backgroundColor: "#ffffff",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontFamily: "inherit",
  outline: "none",
};

/** Format a Date as a datetime-local input value (local time) */
function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Format a Date as YYYY-MM-DD (local time) */
function toDateInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

type SubmitResult =
  | { kind: "success" }
  | {
      kind: "blocked";
      administrationId: string;
      missingDemographics: string[];
    };

export function RecordAdministrationModal({
  isOpen,
  travelerId,
  protocolKey,
  vaccineName,
  recommendationSelectionId,
  onClose,
  onRecorded,
}: RecordAdministrationModalProps) {
  const [refCodes, setRefCodes] = useState<CairReferenceCodes | null>(null);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codesError, setCodesError] = useState<string | null>(null);

  // Form state
  const [cvxCode, setCvxCode] = useState("");
  const [mvxCode, setMvxCode] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [doseAmount, setDoseAmount] = useState("0.5");
  const [doseUnits, setDoseUnits] = useState("mL");
  const [routeCode, setRouteCode] = useState("IM");
  const [bodySiteCode, setBodySiteCode] = useState("");
  const [visPublicationDate, setVisPublicationDate] = useState("");
  const [visPresentedDate, setVisPresentedDate] = useState(() =>
    toDateInput(new Date())
  );
  const [administeredAt, setAdministeredAt] = useState(() =>
    toDatetimeLocal(new Date())
  );
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [showDemographicsForm, setShowDemographicsForm] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  // Mappings for this protocol (may be multiple CVX options)
  const protocolMappings = useMemo<CvxMapping[]>(() => {
    if (!refCodes) return [];
    return refCodes.cvx_mappings.filter(
      (m) => m.protocol_key === protocolKey
    );
  }, [refCodes, protocolKey]);

  const selectedMapping = useMemo(
    () => protocolMappings.find((m) => m.cvx_code === cvxCode),
    [protocolMappings, cvxCode]
  );

  // Reset form + load reference codes when the modal opens
  useEffect(() => {
    if (!isOpen) return;

    setLotNumber("");
    setExpirationDate("");
    setDoseAmount("0.5");
    setDoseUnits("mL");
    setVisPublicationDate("");
    setVisPresentedDate(toDateInput(new Date()));
    setAdministeredAt(toDatetimeLocal(new Date()));
    setNotes("");
    setSubmitError(null);
    setResult(null);
    setShowDemographicsForm(false);

    setLoadingCodes(true);
    setCodesError(null);
    cairService
      .getReferenceCodes()
      .then(setRefCodes)
      .catch(() => setCodesError("Failed to load CAIR reference codes."))
      .finally(() => setLoadingCodes(false));
  }, [isOpen]);

  // Preselect CVX (default mapping) and MVX once codes are loaded
  useEffect(() => {
    if (!isOpen || !refCodes) return;
    const mappings = refCodes.cvx_mappings.filter(
      (m) => m.protocol_key === protocolKey
    );
    const defaultMapping = mappings.find((m) => m.is_default) || mappings[0];
    setCvxCode(defaultMapping?.cvx_code || "");
    setMvxCode(defaultMapping?.default_mvx_code || "");
  }, [isOpen, refCodes, protocolKey]);

  // Keep MVX in sync with the selected CVX mapping's default
  const handleCvxChange = (code: string) => {
    setCvxCode(code);
    const mapping = protocolMappings.find((m) => m.cvx_code === code);
    if (mapping?.default_mvx_code) {
      setMvxCode(mapping.default_mvx_code);
    }
  };

  // Body sites available for the selected route
  const availableSites = useMemo<string[]>(() => {
    if (!refCodes) return [];
    return (
      refCodes.sites_by_route[routeCode] || Object.keys(refCodes.body_sites)
    );
  }, [refCodes, routeCode]);

  // Clear body site if it is no longer valid for the selected route
  useEffect(() => {
    if (bodySiteCode && !availableSites.includes(bodySiteCode)) {
      setBodySiteCode("");
    }
  }, [availableSites, bodySiteCode]);

  if (!isOpen) return null;

  const displayName =
    vaccineName || selectedMapping?.cvx_description || protocolKey;

  const handleSubmit = async () => {
    if (!cvxCode) {
      setSubmitError("A CVX code is required.");
      return;
    }
    if (!lotNumber.trim()) {
      setSubmitError("Lot number is required.");
      return;
    }
    if (!expirationDate) {
      setSubmitError("Expiration date is required.");
      return;
    }
    if (!administeredAt) {
      setSubmitError("Administration date/time is required.");
      return;
    }

    const payload: RecordAdministrationRequest = {
      protocol_key: protocolKey,
      cvx_code: cvxCode,
      lot_number: lotNumber.trim(),
      expiration_date: expirationDate,
      administered_at: new Date(administeredAt).toISOString(),
      information_source: "00", // new administration
    };
    if (recommendationSelectionId) {
      payload.recommendation_selection_id = recommendationSelectionId;
    }
    if (mvxCode) payload.mvx_code = mvxCode;
    if (doseAmount && !isNaN(Number(doseAmount))) {
      payload.dose_amount = Number(doseAmount);
    }
    if (doseUnits.trim()) payload.dose_units = doseUnits.trim();
    if (routeCode) payload.route_code = routeCode;
    if (bodySiteCode) payload.body_site_code = bodySiteCode;
    if (visPublicationDate) payload.vis_publication_date = visPublicationDate;
    if (visPresentedDate) payload.vis_presented_date = visPresentedDate;
    if (notes.trim()) payload.notes = notes.trim();

    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await cairService.recordAdministration(
        travelerId,
        payload
      );
      notifyAdministrationsUpdated();
      onRecorded?.();
      if (response.cair_status === "blocked_missing_data") {
        setResult({
          kind: "blocked",
          administrationId: response.administration_id,
          missingDemographics: response.missing_demographics || [],
        });
      } else {
        setResult({ kind: "success" });
      }
    } catch (err) {
      console.error("Failed to record administration:", err);
      setSubmitError("Failed to record the administration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubmit = async () => {
    if (result?.kind !== "blocked") return;
    setResubmitting(true);
    setSubmitError(null);
    try {
      const response = await cairService.resubmitCairSubmission(
        result.administrationId
      );
      notifyAdministrationsUpdated();
      if (response.cair_status === "blocked_missing_data") {
        setResult({
          kind: "blocked",
          administrationId: result.administrationId,
          missingDemographics: response.missing_demographics || [],
        });
        setSubmitError(
          "Still missing required patient information. Please complete the fields listed above."
        );
      } else {
        setResult({ kind: "success" });
      }
    } catch (err) {
      console.error("Failed to resubmit to CAIR:", err);
      setSubmitError("Failed to resubmit to CAIR. Please try again.");
    } finally {
      setResubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" style={{ zIndex: 100000 }} onClick={onClose}>
        <div
          className="modal"
          style={{ maxWidth: "620px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2>Record Administration — {displayName}</h2>

          {/* Success confirmation */}
          {result?.kind === "success" ? (
            <div>
              <div
                style={{
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  marginBottom: "16px",
                  fontSize: "14px",
                  color: "#166534",
                }}
              >
                <strong>Administration recorded.</strong> The record has been
                queued for submission to CAIR.
              </div>
              <div className="modal-actions">
                <button onClick={onClose}>Done</button>
              </div>
            </div>
          ) : result?.kind === "blocked" ? (
            /* Blocked on missing demographics */
            <div>
              <div
                style={{
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  marginBottom: "16px",
                  fontSize: "14px",
                  color: "#92400e",
                }}
              >
                <strong>Administration recorded</strong>, but the CAIR
                submission is blocked because the following patient details are
                missing:
                <ul style={{ margin: "8px 0 0 18px" }}>
                  {result.missingDemographics.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>

              {submitError && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    fontSize: "13px",
                    color: "#991b1b",
                  }}
                >
                  {submitError}
                </div>
              )}

              <div className="modal-actions">
                <button onClick={() => setShowDemographicsForm(true)}>
                  Fix Patient Info
                </button>
                <button onClick={handleResubmit} disabled={resubmitting}>
                  {resubmitting ? "Resubmitting..." : "Resubmit to CAIR"}
                </button>
                <button onClick={onClose}>Close</button>
              </div>
            </div>
          ) : loadingCodes ? (
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              Loading CAIR reference codes...
            </p>
          ) : (
            /* Administration form */
            <div>
              {codesError && (
                <div
                  style={{
                    backgroundColor: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    fontSize: "13px",
                    color: "#92400e",
                  }}
                >
                  {codesError} CVX/manufacturer options may be unavailable.
                </div>
              )}

              <div
                className="grid grid-cols-1 sm:grid-cols-2"
                style={{ gap: "12px", marginBottom: "16px" }}
              >
                <div>
                  <label style={labelStyle}>Vaccine (CVX) *</label>
                  {protocolMappings.length > 1 ? (
                    <select
                      value={cvxCode}
                      onChange={(e) => handleCvxChange(e.target.value)}
                      style={inputStyle}
                    >
                      {protocolMappings.map((m) => (
                        <option key={m.cvx_code} value={m.cvx_code}>
                          {m.cvx_description} (CVX {m.cvx_code})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={
                        selectedMapping
                          ? `${selectedMapping.cvx_description} (CVX ${selectedMapping.cvx_code})`
                          : cvxCode
                      }
                      onChange={(e) => {
                        // No mapping found for this protocol — allow manual CVX entry
                        if (protocolMappings.length === 0) {
                          setCvxCode(e.target.value);
                        }
                      }}
                      readOnly={protocolMappings.length > 0}
                      placeholder="CVX code"
                      style={{
                        ...inputStyle,
                        backgroundColor:
                          protocolMappings.length > 0 ? "#f9fafb" : "#ffffff",
                      }}
                    />
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Manufacturer (MVX)</label>
                  <select
                    value={mvxCode}
                    onChange={(e) => setMvxCode(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Select…</option>
                    {Object.entries(refCodes?.manufacturers || {}).map(
                      ([code, name]) => (
                        <option key={code} value={code}>
                          {name} ({code})
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Lot Number *</label>
                  <input
                    type="text"
                    value={lotNumber}
                    onChange={(e) => setLotNumber(e.target.value)}
                    placeholder="e.g., A123456"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Expiration Date *</label>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div
                  className="grid grid-cols-2"
                  style={{ gap: "12px" }}
                >
                  <div>
                    <label style={labelStyle}>Dose Amount</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={doseAmount}
                      onChange={(e) => setDoseAmount(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Units</label>
                    <input
                      type="text"
                      value={doseUnits}
                      onChange={(e) => setDoseUnits(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div
                  className="grid grid-cols-2"
                  style={{ gap: "12px" }}
                >
                  <div>
                    <label style={labelStyle}>Route</label>
                    <select
                      value={routeCode}
                      onChange={(e) => setRouteCode(e.target.value)}
                      style={inputStyle}
                    >
                      {Object.entries(refCodes?.routes || {}).map(
                        ([code, name]) => (
                          <option key={code} value={code}>
                            {name} ({code})
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Body Site</label>
                    <select
                      value={bodySiteCode}
                      onChange={(e) => setBodySiteCode(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Select…</option>
                      {availableSites.map((code) => (
                        <option key={code} value={code}>
                          {refCodes?.body_sites[code] || code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>VIS Published Date</label>
                  <input
                    type="date"
                    value={visPublicationDate}
                    onChange={(e) => setVisPublicationDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>VIS Presented Date</label>
                  <input
                    type="date"
                    value={visPresentedDate}
                    onChange={(e) => setVisPresentedDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label style={labelStyle}>Administered At *</label>
                  <input
                    type="datetime-local"
                    value={administeredAt}
                    onChange={(e) => setAdministeredAt(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label style={labelStyle}>Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                    rows={2}
                    style={{ ...inputStyle, resize: "vertical", marginBottom: 0 }}
                  />
                </div>
              </div>

              {submitError && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    fontSize: "13px",
                    color: "#991b1b",
                  }}
                >
                  {submitError}
                </div>
              )}

              <div className="modal-actions">
                <button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Recording..." : "Record & Submit to CAIR"}
                </button>
                <button onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Demographics fix dialog (opened from the blocked warning) */}
      <CairDemographicsForm
        isOpen={showDemographicsForm}
        travelerId={travelerId}
        missingFields={
          result?.kind === "blocked" ? result.missingDemographics : []
        }
        onClose={() => setShowDemographicsForm(false)}
        onSaved={handleResubmit}
      />
    </>
  );
}

export default RecordAdministrationModal;
